'use client';

import React, {FC} from 'react';
import cn from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import {usePathname} from 'next/navigation';

import {Button, renderUtils} from '@zoefin/design-system';

import {useFormContext} from '../../form';

import styles from './styles.module.scss';

const STEPS = [
  'Fill out the form',
  'Get advisor matches',
  'Book a free consultation',
];

const StepIntro: FC = () => {
  const {additionalProps, next} = useFormContext();
  const pathname = usePathname();

  renderUtils.useMount(() => {
    // Skip this step if `skip` includes `intro`
    if (additionalProps.skip.includes('intro')) next();
  });

  // Do not render this step if `skip` includes `intro`
  if (additionalProps.skip.includes('intro')) return null;

  const isNerdwallet = pathname.includes('/forms/nerdwallet');
  const isUsaToday = pathname.includes('/forms/usa-today');
  const isForbesAdvisor = pathname.includes('/forms/forbes-advisor');
  const isMoneyTalksNews = pathname.includes('/forms/moneytalksnews');
  const isMyBankTracker = pathname.includes('/forms/mybanktracker');
  const isCNN = pathname.includes('/forms/cnn');

  const isAffiliate =
    isNerdwallet ||
    isUsaToday ||
    isForbesAdvisor ||
    isMoneyTalksNews ||
    isMyBankTracker ||
    isCNN;

  let affiliateLogo: string;
  let logoTestId: string;

  if (isNerdwallet) {
    affiliateLogo = '/static/svg/partners/nerdwallet.svg';
    logoTestId = 'nerdwallet-logo';
  } else if (isUsaToday) {
    affiliateLogo = '/static/img/partners/usa-today.png';
    logoTestId = 'usa-today-logo';
  } else if (isForbesAdvisor) {
    affiliateLogo = '/static/img/partners/forbes-advisor.png';
    logoTestId = 'forbes-logo';
  } else if (isMyBankTracker) {
    affiliateLogo = '/static/img/partners/mybanktracker.svg';
    logoTestId = 'mybanktracker-logo';
  } else if (isCNN) {
    affiliateLogo = '/static/img/partners/cnn-shape-black.png';
    logoTestId = 'cnn-logo';
  }

  return (
    <div className={styles.stepIntro}>
      <div className={styles.stepIntro__topContainer}>
        <div className={styles.stepIntro__logo}>
          {isAffiliate && affiliateLogo?.length ? (
            <>
              <figure
                className={cn(styles.stepIntro__logo_affiliate, {
                  [styles.stepIntro__logo_affiliate__modMyBankTracker]:
                    isMyBankTracker,
                  [styles.stepIntro__logo_affiliate__modCNN]: isCNN,
                })}>
                <Image
                  src={affiliateLogo}
                  alt="affiliate logo"
                  layout="fill"
                  style={{width: '100%', height: '100%'}}
                  data-testid={logoTestId}
                />
              </figure>
              <figure className={styles.stepIntro__logo_zoe}>
                <Image
                  src="/static/img/affiliates/zoe-logo.svg"
                  alt="Zoe logo"
                  style={{width: '100%', height: '100%'}}
                  layout="fill"
                />
              </figure>
            </>
          ) : (
            <Image
              src="/static/img/affiliates/zoe-logo.svg"
              alt="Zoe logo"
              width={80}
              height={35}
            />
          )}
        </div>
        <h1 className={styles.stepIntro__title}>
          {isAffiliate
            ? 'Find a Trusted Financial Advisor'
            : 'An Easy Way To Find A Financial Advisor'}
        </h1>
        <h3 className={styles.stepIntro__description}>
          {isAffiliate ? (
            <>
              Fill out this 3 minute form to connect with
              <b>vetted fiduciary advisors</b>.
            </>
          ) : (
            <>
              Fill out this 2 minute form to connect with
              <b>your top advisor matches</b>.
            </>
          )}
        </h3>

        <ul className={styles.stepper}>
          {STEPS.map((step, idx) => (
            <li
              key={step}
              className={cn(styles.stepper__step, {
                [styles.stepper__step__modActive]: idx === 0,
              })}>
              <div
                className={cn(styles.stepper__step_circle, {
                  [styles.stepper__step_circle__modActive]: idx === 0,
                })}
              />
              {step}
            </li>
          ))}
        </ul>
      </div>

      <Button as="button" onClick={next}>{`Let's start`}</Button>

      <div className={styles.stepIntro__footer}>
        <div className={styles.stepIntro__footer_login}>
          Already completed this form?
          <Link href="/login">Log In</Link>
        </div>
        <div className={styles.stepIntro__footer_security}>
          <div>
            <Image
              src="/static/svg/flat/icon_lock_darkblue.svg"
              alt="lock icon"
              width={12}
              height={14}
            />
          </div>
          Your information is secure.
        </div>
      </div>
    </div>
  );
};

export default StepIntro;
